import React, { FC, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    OrderProductCategoryList,
    SELECT_ALL,
} from '@component/OrderProductCategoryList';
import { GetResponseOrderParts, PartsProduct } from '@app/react/const/type';
import { OnSelectProductHandler } from '@app/react/uikit';
import {
    filterProducts,
    reduceProductWithCategory,
} from '@root/app/react/common/Function';
import { useWindowScrollPosition } from '@app/react/hooks/useWindowScrollPosition';
import { useSessionStorage } from '@app/react/hooks/useSessionStorage';
import type { AppState } from '@app/react/reducer';
import { noProduct, noProductCategory } from '@app/react/const/products/noProduct';
import { buildCategoryLength } from '@app/react/common/buildCategoryLength';

type H = React.ChangeEventHandler<HTMLSelectElement>;

const selectFeatherinside = (state: AppState): GetResponseOrderParts | null => (
    state.APIData.featherinside);

interface Props {
    selectedProduct: PartsProduct | null;
    onSelectProduct: OnSelectProductHandler;
    allDisabled?: boolean;
}

export const FeatherinsideOrderProductCategoryListContainer: FC<Props> = ({
    selectedProduct,
    onSelectProduct,
}) => {
    const scrollObj = useRef(null);
    useWindowScrollPosition('scroll_featherinside_opclc', true, scrollObj);

    const [
        selectCategoryId,
        setSelectCategoryId,
    ] = useSessionStorage('cat_featherinside_opclc', SELECT_ALL);

    const productData = useSelector(selectFeatherinside);

    const categoryData = useMemo(() => {
        const cd = productData?.categoryData ?? [];
        const pd = productData?.partsData ?? [];
        return buildCategoryLength(
            [noProductCategory, ...cd],
            [noProduct, ...pd],
        );
    }, [productData]);

    const viewData = useMemo(() => {
        const items = productData?.partsData ?? [];
        const newPartsData = filterProducts(
            [noProduct, ...items],
            selectCategoryId,
            SELECT_ALL,
        );
        return reduceProductWithCategory(newPartsData);
    }, [productData, selectCategoryId]);

    const categoryChange: H = useCallback((event) => {
        setSelectCategoryId(parseInt(event.target.value, 10));
    }, [setSelectCategoryId]);

    return (
        <OrderProductCategoryList
            rootRef={scrollObj}
            categoryId={selectCategoryId}
            categoryData={categoryData}
            categoryChange={categoryChange}
            selectedProduct={selectedProduct}
            onSelectProduct={onSelectProduct}
            viewData={viewData}
            disableSubCategory
        />
    );
};
